import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';
import { COMPANY_INFO } from '../lib/utils';
import { getMainCities, cityPath, SAP_NUMBER } from '../lib/cities-data';

const footerLinks = [
  {
    title: 'Services',
    links: [
      { label: 'Entretien ménager', href: '/services/entretien-menager' },
      { label: 'Jardinage', href: '/services/espaces-verts' },
      { label: 'Taille de haies', href: '/taille-de-haies' },
      { label: 'Repassage', href: '/services/entretien-linge' },
      { label: 'Petit bricolage', href: '/services/petit-bricolage' },
      { label: 'Tous les services', href: '/services' },
      { label: 'Global Domicile', href: '/services/global-service-domicile' },
      { label: 'Global Jardin', href: '/services/global-service-jardin' },
    ]
  },
  {
    title: 'Villes desservies',
    links: [
      ...getMainCities().slice(0, 8).map((c) => ({ label: c.name, href: cityPath(c) })),
      { label: 'Toutes les villes', href: '/services-a-domicile' },
    ]
  },
  {
    title: 'Informations',
    links: [
      { label: 'Avance immédiate', href: '/credit-impot' },
      { label: 'Demander un devis', href: '/devis' },
      { label: 'Ptitconseils', href: '/ptitconseils' },
      { label: 'Blog', href: '/blog' },
      { label: 'Chouette Apprentissage offert', href: '/chouette-apprentissage' },
      { label: 'Prendre rendez-vous', href: '/rendez-vous' },
      { label: 'Support', href: '/contact' },
      { label: 'Agence Artois', href: '/agence/artois.ptitservices' },
      { label: 'Devenir Easer (recrutement)', href: '/devenir-easer' },
      { label: 'Espace client', href: '/client' },
    ]
  }
];

export default function Footer() {
  return (
    <footer className="border-t border-border bg-[hsl(var(--secondary))]/40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="https://customer-assets.emergentagent.com/job_easers-pro/artifacts/j38s08lp_LOGOSITE.png" alt="Ptitservices.com - Facilitateur de vies" className="h-12 w-auto rounded-lg" />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mb-6">
              Votre partenaire de confiance pour tous vos services à la personne dans l'Artois. Crédit d'impôt 50% avec avance immédiate.
            </p>
            <div className="space-y-2">
              <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="flex items-center gap-2 text-sm text-foreground hover:text-[hsl(var(--primary))] transition-colors" data-testid="footer-phone-link">
                <Phone className="h-4 w-4 text-[hsl(var(--primary))]" />
                {COMPANY_INFO.phone}
              </a>
              <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-2 text-sm text-foreground hover:text-[hsl(var(--primary))] transition-colors" data-testid="footer-email-link">
                <Mail className="h-4 w-4 text-[hsl(var(--primary))]" />
                {COMPANY_INFO.email}
              </a>
              <div className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="footer-address">
                <MapPin className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" />
                <span>{COMPANY_INFO.address}<br />{COMPANY_INFO.city}</span>
              </div>
            </div>
          </div>

          {/* Links */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-4">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} translate="no" className="notranslate flex items-center gap-1 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors">
                      <ChevronRight className="h-3 w-3" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-border">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} {COMPANY_INFO.name}. Tous droits réservés.</p>
            <div className="flex items-center gap-4">
              <span className="px-2 py-1 rounded bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] font-medium">Crédit d'impôt 50%</span>
              <span className="px-2 py-1 rounded bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] font-medium">Avance immédiate Urssaf</span>
              <span className="px-2 py-1 rounded bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] font-medium">Zone Artois</span>
              <span className="px-2 py-1 rounded bg-[hsl(var(--brand-mint))] text-[hsl(var(--brand-ink))] font-medium" data-testid="footer-sap-number">N° {SAP_NUMBER}</span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mt-4 text-xs text-muted-foreground">
            <Link to="/mentions-legales" className="hover:text-[hsl(var(--primary))] transition-colors">Mentions légales</Link>
            <span>|</span>
            <Link to="/politique-confidentialite" className="hover:text-[hsl(var(--primary))] transition-colors">Politique de confidentialité</Link>
            <span>|</span>
            <Link to="/cookies" className="hover:text-[hsl(var(--primary))] transition-colors">Politique de cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
